<template>
  <div
    class="c-saved-canvas-management-toolbar flex flex-wrap justify-between items-start gap-4 pa-4 rounded-2 bg-base"
  >
    <div v-if="view === 'smartFolders'" class="flex items-center gap-2 flex-1">
      <CSearch
        class="min-w-[280px]"
        :initial-search-value="search"
        :placeholder="t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.searchInAllFields')"
        size="large"
        @update:modelValue="emit('search', $event)"
      />
      <CHelpText>
        {{ t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.searchHelpText') }}
      </CHelpText>
    </div>
    <div v-else class="flex flex-wrap items-center gap-2 flex-1">
      <CSearchFilters
        :customFilterComponents="!!launchDarkly.enable_saved_canvases_filters ? customFilterComponents : undefined"
        :disabled="!!isLoadingItems"
        entity="canvas"
        :filters="metadataFilterValues"
        hide-metadata-filters
        include-draft-values
        :initial-search-value="search"
        :owner-id="pitcherInfo?.isAnyTypeOfAdmin ? null : myUser?.id"
        :placeholder="t('canvasUI.components.templates.CSavedCanvasesManagementToolbar.searchInNameOrTags')"
        @update:filters="emit('update:filter-values', $event)"
        @update:search="emit('search', $event)"
      />
    </div>
    <div class="flex gap-4">
      <CButton
        v-if="launchDarkly.enable_smart_folders"
        v-tooltip="
          view === 'smartFolders'
            ? t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.viewAsTable')
            : t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.viewAsSmartFolders')
        "
        circle
        icon
        icon-placement="left"
        @click="toggleViewMode"
      >
        <template #icon>
          <CIcon fa-type="fas" :icon="view === 'smartFolders' ? 'table-list' : 'folder-bookmark'" size="16px" />
        </template>
      </CButton>
      <CButton
        v-if="launchDarkly.enable_smart_folders && view === 'smartFolders'"
        v-tooltip="t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.configureSmartFolders')"
        circle
        icon
        icon-placement="left"
        @click="emit('configureSmartFolders')"
      >
        <template #icon>
          <CIcon icon="sliders-simple" size="16px" />
        </template>
      </CButton>
      <CTableColumnsSettings
        v-if="!hideView && view === 'normal'"
        :table-columns-settings="tableColumnsSettings"
        :tooltip-text="t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.configureColumns')"
        @move="(payload) => emit('move', payload)"
        @restore="emit('restoreColumns')"
        @select="(payload) => emit('select', payload)"
      />
      <CButton v-if="!hideCreate" icon-placement="left" type="primary" @click="$emit('create:canvas')">
        {{ t('canvasUI.components.savedCanvases.CSavedCanvasesManagementToolbar.createCanvas') }}
        <template #icon>
          <CIcon icon="plus" size="16px" />
        </template>
      </CButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject, type ComputedRef } from 'vue'
import { useI18n } from 'vue-i18n'

import CButton from '@lib/components/CButton/CButton.vue'
import CHelpText from '@lib/components/CHelpText/CHelpText.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'
import CSearch from '@lib/components/CSearch/CSearch.vue'
import CSearchFilters from '@lib/components/CSearchFilters/CSearchFilters.vue'
import CTableColumnsSettings, {
  type TableColumnSetting,
} from '@lib/components/CTableColumnsSettings/CTableColumnsSettings.vue'

import useSmartFolders from '@lib/components/savedCanvases/smartFolders/SmartFolders.use'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { MetadataTemplateFieldTypeEnum, User } from '@openapi'
import type { PitcherInfo } from '@lib/types/app'

defineProps<{
  search: string
  metadataFilterValues: Record<string, any>
  tableColumnsSettings: TableColumnSetting[]
  customFilterComponents: {
    type: MetadataTemplateFieldTypeEnum
    name: string
    label: string
    options?: { label: string; value: string }[]
  }[]
  isLoadingItems?: boolean
  hideCreate?: boolean
  hideView?: boolean
}>()

const emit = defineEmits<{
  search: [s: string]
  'create:canvas': []
  'update:filter-values': [mf: Record<string, any>]
  move: [payload: { targetItemId: TableColumnSetting['id']; itemId: string }]
  select: [payload: { id: TableColumnSetting['id']; checked: boolean }]
  restoreColumns: []
  configureSmartFolders: []
}>()
const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)
const myUser = inject<ComputedRef<User | null>>('myUser')
const pitcherInfo = inject<ComputedRef<PitcherInfo>>(
  'pitcherInfo',
  computed(() => ({
    accessToken: '',
    instanceId: '',
    apiOrigin: '',
    isAnyTypeOfAdmin: false,
  })),
)

const { t } = useI18n()
const { toggleViewMode, isSmartFoldersView } = useSmartFolders()
const view = computed(() =>
  isSmartFoldersView.value && launchDarkly.value.enable_smart_folders ? 'smartFolders' : 'normal',
)
</script>
